import { QueryClient, QueryFunction } from "@tanstack/react-query";
import { csrfService } from "./csrfService";
import { SecurityHeadersValidator, ErrorSanitizer, SecurityConfig } from "./security";
import { env } from "./environment";


// Global flag to prevent multiple redirects
let isRedirecting = false;

// Track if we're currently validating a session
let isValidatingSession = false;

// Handle 401 errors globally
function handle401Error() {
  // Don't redirect if we're already redirecting or validating
  if (isRedirecting || isValidatingSession) return;
  
  // Don't redirect if we're already on the login page
  if (window.location.pathname === '/login' || window.location.pathname === '/register') {
    return;
  }
  
  isRedirecting = true;
  
  // Clear session data
  localStorage.removeItem("sessionId");
  localStorage.removeItem("user");
  localStorage.removeItem("lastActivity");
  
  // Small delay to ensure all pending requests complete
  setTimeout(() => {
    window.location.href = "/login";
    // Reset flag after redirect
    setTimeout(() => {
      isRedirecting = false;
    }, 1000);
  }, 100);
}

// Update last activity timestamp
export function updateLastActivity() {
  localStorage.setItem("lastActivity", Date.now().toString());
}

// Export for use in auth hook
export function setValidatingSession(validating: boolean) {
  isValidatingSession = validating;
  console.log("Session validation state:", validating);
}

export const getValidatingSession = () => isValidatingSession;

async function throwIfResNotOk(res: Response) {
  if (!res.ok) {
    const text = (await res.text()) || res.statusText;
    throw new Error(`${res.status}: ${text}`);
  }
}

export async function apiRequest(
  method: string,
  url: string,
  data?: unknown | undefined,
  options?: RequestInit
): Promise<Response> {
  // Construct full URL using the API base URL
  const fullUrl = url.startsWith('http') ? url : `${env.apiUrl}${url}`;
  
  const defaultHeaders: Record<string, string> = data 
    ? { "Content-Type": "application/json" } 
    : {};

  // Always include authorization header if available in localStorage
  const sessionId = localStorage.getItem("sessionId");
  if (sessionId) {
    defaultHeaders["Authorization"] = `Bearer ${sessionId}`;
  }

  // Add CSRF token for state-changing operations
  // Re-enabled with proper backend implementation
  if (csrfService.requiresCSRF(method) && sessionId) {
    try {
      const csrfToken = await csrfService.getCSRFToken();
      defaultHeaders["X-CSRF-Token"] = csrfToken;
      console.log(`🛡️ CSRF token added for ${method} ${url}`);
    } catch (error) {
      console.error("❌ Failed to get CSRF token:", error);
      // Don't fail the request if CSRF token fetch fails in case the endpoint is exempted
      // The server will return 403 if CSRF is required but missing
    }
  }

  // Get additional headers if present in options
  const customHeaders = options?.headers as Record<string, string> || {};
  
  // Set longer timeout for auth endpoints (30 seconds) and shorter for others (15 seconds)
  const isAuthEndpoint = url.includes('/auth/');
  const isEmailRefreshEndpoint = url.includes('/refresh-emails');
  const timeoutMs = isAuthEndpoint ? 30000 : 
                   isEmailRefreshEndpoint ? 1200000 : // 20 minutes for email refresh
                   15000;
  
  // Create modified options without headers to prevent duplication
  const modifiedOptions = { ...options };
  delete modifiedOptions?.headers;
  
  // Create AbortController for timeout
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
  
  try {
    // Simplified fetch options for better compatibility with Render
    const fetchOptions: RequestInit = {
      method,
      headers: { ...defaultHeaders, ...customHeaders },
      body: data ? JSON.stringify(data) : undefined,
      signal: controller.signal,
      // Remove problematic CORS settings that can cause empty responses
      ...modifiedOptions
    };
    
    console.log(`Making ${method} request to: ${fullUrl}`);
    console.log('Request headers:', fetchOptions.headers);
    
    const res = await fetch(fullUrl, fetchOptions);
    
    clearTimeout(timeoutId);
    
    console.log(`Response status: ${res.status}`);
    console.log('Response headers:', Object.fromEntries(res.headers.entries()));
    
    // Update activity timestamp on successful request
    if (res.status !== 401 && sessionId) {
      updateLastActivity();
    }
    
    // SECURITY: Validate security headers on API responses
    if (SecurityConfig.SETTINGS.enableHeaderValidation) {
      SecurityHeadersValidator.validateSecurityHeaders(res);
    }
    
    // More robust response handling:
    const contentType = res.headers.get("content-type");
    const contentLength = res.headers.get("content-length");

    if (res.status === 200 && contentLength === '0' && !contentType) {
      console.warn('[Warning] Backend returned 200 with empty body (possible CORS proxy quirk). Treating as no content.');
      // For login/register, this should be an error, but for other endpoints, it might be valid (e.g., 204 No Content)
      // We'll let the calling function decide if empty content is an error based on context.
      // For now, we return the response object, and the .json() call later will fail if body is truly empty.
      return res;
    }

    if (contentType && contentType.includes("application/json")) {
      return res; // Let the caller call .json()
    } else if (res.status >= 200 && res.status < 300 && (!contentType || !contentType.includes("application/json"))) {
      // Successful status code but not JSON
      console.warn(`Response was successful but not JSON. Content-Type: ${contentType}`);
      // If it's a success response but not JSON, and not an empty body case we already handled,
      // it might be an issue. Let's allow the .text() or other methods to be called by the user.
      // However, for auth, we expect JSON. If this path is hit during auth, it's an error.
      if (isAuthEndpoint) {
        const responseText = await res.text(); // try to get some text for debugging
        const sanitizedError = ErrorSanitizer.sanitizeErrorMessage(`Invalid response from server: Expected JSON but received Content-Type: ${contentType}. Response text: ${responseText.substring(0, 100)}`);
        throw new Error(sanitizedError);
      }
      return res;
    } else {
      // For other errors, return the response as is
      return res;
    }

  } catch (error: any) {
    clearTimeout(timeoutId);
    if (error.name === 'AbortError') {
      const sanitizedError = ErrorSanitizer.sanitizeErrorMessage(`Request timeout after ${timeoutMs / 1000} seconds`);
      throw new Error(sanitizedError);
    }
    
    // SECURITY: Sanitize error messages to prevent information disclosure
    if (SecurityConfig.SETTINGS.enableErrorSanitization) {
      const sanitizedError = ErrorSanitizer.sanitizeErrorMessage(error);
      throw new Error(sanitizedError);
    }
    
    throw error;
  }
}

type UnauthorizedBehavior = "returnNull" | "throw";
export const getQueryFn: <T>(options: {
  on401: UnauthorizedBehavior;
}) => QueryFunction<T> =
  ({ on401: unauthorizedBehavior }) =>
  async ({ queryKey }) => {
    // Add Authorization header if session exists
    const headers: Record<string, string> = {};
    const sessionId = localStorage.getItem("sessionId");
    
    if (sessionId) {
      headers["Authorization"] = `Bearer ${sessionId}`;
    }
    
    // Check if there are filter parameters (queryKey[1])
    let url = queryKey[0] as string;
    if (queryKey.length > 1 && queryKey[1] && typeof queryKey[1] === 'object') {
      const filters = queryKey[1] as Record<string, string>;
      if (Object.keys(filters).length > 0) {
        const queryParams = new URLSearchParams();
        Object.entries(filters).forEach(([key, value]) => {
          if (value) queryParams.append(key, value);
        });
        
        if (queryParams.toString()) {
          url = `${url}?${queryParams.toString()}`;
        }
      }
    }
    
    // Construct full URL using the API base URL
    const fullUrl = url.startsWith('http') ? url : `${env.apiUrl}${url}`;
    
    // Set timeout for queries (15 seconds)
    const isEmailRefreshEndpoint = url.includes('/refresh-emails');
    const timeoutMs = isEmailRefreshEndpoint ? 1200000 : 15000; // 20 minutes for email refresh, 15 seconds for others
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
    
    try {
      const res = await fetch(fullUrl, {
        headers,
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      if (unauthorizedBehavior === "returnNull" && res.status === 401) {
        return null;
      }

      // Handle 401 for queries (but be smart about it)
      if (res.status === 401) {
        // Only redirect if we have a stored session (user thinks they're logged in)
        const sessionId = localStorage.getItem("sessionId");
        if (sessionId && !isValidatingSession) {
          handle401Error();
        }
        throw new Error("Unauthorized");
      }

      await throwIfResNotOk(res);
      return await res.json();
    } catch (error: any) {
      clearTimeout(timeoutId);
      if (error.name === 'AbortError') {
        throw new Error(`Request timeout after ${timeoutMs / 1000} seconds`);
      }
      throw error;
    }
  };

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryFn: getQueryFn({ on401: "throw" }),
      refetchInterval: false,
      refetchOnWindowFocus: false,
      // Improved caching strategy for better performance
      staleTime: 5 * 60 * 1000, // 5 minutes - data is fresh for 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes - keep in cache for 10 minutes
      retry: (failureCount, error: any) => {
        // Don't retry on auth errors at all - these need immediate attention
        if (error?.message?.includes('401') || error?.message?.includes('403') || error?.message?.includes('Unauthorized')) {
          console.warn('Authentication error, not retrying:', error?.message);
          return false;
        }
        // For 503 errors, retry up to 2 times
        if (error?.message?.includes('503')) {
          return failureCount < 2;
        }
        // Retry up to 1 time for other errors to avoid overwhelming the server
        return failureCount < 1;
      },
      retryDelay: (attemptIndex, error: any) => {
        // Shorter delays to avoid long waits
        if (error?.message?.includes('503')) {
          return Math.min(1000 * 2 ** attemptIndex, 5000); // Max 5 seconds
        }
        return Math.min(1000 * 2 ** attemptIndex, 3000); // Max 3 seconds for others
      },
    },
    mutations: {
      retry: false,
      onError: (error: any) => {
        // Handle 401 errors in mutations
        if (error?.message?.includes('401') || error?.message?.includes('Unauthorized')) {
          handle401Error();
        }
      }
    },
  },
});

// Prefetch function for critical data
export const prefetchCriticalData = async () => {
  const sessionId = localStorage.getItem("sessionId");
  if (!sessionId) return;

  // Prefetch all critical data in parallel for faster dashboard loading
  const prefetchPromises = [
    queryClient.prefetchQuery({
      queryKey: ["/api/players/"],
      staleTime: 5 * 60 * 1000,
    }),
    queryClient.prefetchQuery({
      queryKey: ["/api/shortlists/"],
      staleTime: 5 * 60 * 1000,
    }),
    queryClient.prefetchQuery({
      queryKey: ["/api/emails/"],
      staleTime: 5 * 60 * 1000,
    }),
  ];

  try {
    await Promise.allSettled(prefetchPromises);
  } catch (error) {
    console.warn("Failed to prefetch some data:", error);
  }
};

// Invalidate all data function for when user logs out
export const invalidateAllData = () => {
  queryClient.clear();
};
